import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Tabs,
  Tab,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People,
  Block,
  Delete,
  Visibility,
  Security,
  Timeline,
  Warning,
  CheckCircle,
  Cancel,
  Refresh,
  PersonRemove,
  PersonAdd,
  AdminPanelSettings,
  CloudDownload,
  CloudUpload
} from '@mui/icons-material';
import { useActivity } from '../contexts/ActivityContext';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function AdminPanel() {
  const { 
    sessions, 
    activityLog, 
    bannedIPs, 
    kickSession, 
    banIP, 
    unbanIP, 
    getStats 
  } = useActivity();
  
  const { users, addUser, deleteUser, updateUserRole } = useAuth();
  
  const [activeTab, setActiveTab] = useState(0);
  const [kickDialog, setKickDialog] = useState({ open: false, session: null });
  const [banDialog, setBanDialog] = useState({ open: false, ip: '' });
  const [stats, setStats] = useState({});
  const [refreshKey, setRefreshKey] = useState(0);
  
  // User Management State
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newUserRole, setNewUserRole] = useState('user');
  const [deleteUserId, setDeleteUserId] = useState(null);
  const [showAddUserDialog, setShowAddUserDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const { showNotification } = useNotification();

  useEffect(() => {
    setStats(getStats());
    const interval = setInterval(() => {
      setStats(getStats());
      setRefreshKey(prev => prev + 1);
    }, 5000);

    return () => clearInterval(interval);
  }, [getStats, sessions, activityLog]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleKickSession = () => {
    if (kickDialog.session) {
      kickSession(kickDialog.session.id);
      setKickDialog({ open: false, session: null });
    }
  };

  const handleBanIP = () => {
    if (banDialog.ip) {
      banIP(banDialog.ip);
      setBanDialog({ open: false, ip: '' });
    }
  };

  // Export users to JSON file
  const handleExportUsers = () => {
    try {
      const usersData = JSON.stringify(users, null, 2);
      const blob = new Blob([usersData], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      
      const a = document.createElement('a');
      a.href = url;
      a.download = `trauma-users-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      showNotification('Users exported successfully', 'success');
    } catch (error) {
      console.error('Error exporting users:', error);
      showNotification('Failed to export users', 'error');
    }
  };

  // Handle file selection for import
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setImportFile(file);
    }
  };

  // Import users from JSON file
  const handleImportUsers = () => {
    if (!importFile) {
      showNotification('Please select a file to import', 'error');
      return;
    }

    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const importedUsers = JSON.parse(e.target.result);
        
        if (!Array.isArray(importedUsers)) {
          throw new Error('Invalid file format: Expected an array of users');
        }

        // Get existing usernames to prevent duplicates
        const existingUsernames = new Set(users.map(u => u.username));
        const newUsers = importedUsers.filter(user => 
          !existingUsernames.has(user.username)
        );

        if (newUsers.length === 0) {
          showNotification('No new users to import', 'info');
          return;
        }

        // Add new users
        newUsers.forEach(user => {
          addUser(user.username, user.password, user.role);
        });
        
        showNotification(`Successfully imported ${newUsers.length} user(s)`, 'success');
        setShowImportDialog(false);
        setImportFile(null);
      } catch (error) {
        console.error('Error importing users:', error);
        showNotification('Invalid file format or corrupted data', 'error');
      }
    };

    reader.onerror = () => {
      showNotification('Error reading the file', 'error');
    };

    reader.readAsText(importFile);
  };

  // User management functions
  const handleAddUser = () => {
    if (!newUsername.trim() || !newPassword) {
      showNotification('Username and password are required', 'error');
      return;
    }

    try {
      addUser(newUsername, newPassword, newUserRole);
      showNotification(`User ${newUsername} added successfully`, 'success');
      setNewUsername('');
      setNewPassword('');
      setNewUserRole('user');
      setShowAddUserDialog(false);
    } catch (error) {
      console.error('Error adding user:', error);
      showNotification('Failed to add user', 'error');
    }
  };

  const handleDeleteUser = () => {
    if (deleteUserId) {
      deleteUser(deleteUserId);
      showNotification('User deleted successfully', 'success');
      setShowDeleteDialog(false);
      setDeleteUserId(null);
    }
  };

  const formatDuration = (startTime, endTime = null) => {
    const start = new Date(startTime);
    const end = endTime ? new Date(endTime) : new Date();
    const duration = Math.floor((end - start) / 1000 / 60); // minutes
    
    if (duration < 60) return `${duration}m`;
    const hours = Math.floor(duration / 60);
    const minutes = duration % 60;
    return `${hours}h ${minutes}m`;
  };

  const activeSessions = sessions.filter(s => s.status === 'active');

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
        Admin Control Panel
      </Typography>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: 'success.light', color: 'success.contrastText' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <People sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                {stats.activeSessions || 0}
              </Typography>
              <Typography variant="body2">Active Sessions</Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: 'info.light', color: 'info.contrastText' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Timeline sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                {stats.totalSessions || 0}
              </Typography>
              <Typography variant="body2">Total Sessions</Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: 'warning.light', color: 'warning.contrastText' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Security sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                {stats.totalActions || 0}
              </Typography>
              <Typography variant="body2">Total Actions</Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: 'error.light', color: 'error.contrastText' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Block sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                {stats.bannedIPs || 0}
              </Typography>
              <Typography variant="body2">Banned IPs</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Card>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
            },
          }}
        >
          <Tab icon={<People />} label="Active Sessions" iconPosition="start" />
          <Tab icon={<Timeline />} label="Activity Log" iconPosition="start" />
          <Tab icon={<Block />} label="Banned IPs" iconPosition="start" />
          <Tab icon={<AdminPanelSettings />} label="User Management" iconPosition="start" />
        </Tabs>

        {/* Active Sessions Tab */}
        <TabPanel value={activeTab} index={0}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Active User Sessions</Typography>
            <Button
              startIcon={<Refresh />}
              onClick={() => setRefreshKey(prev => prev + 1)}
              size="small"
            >
              Refresh
            </Button>
          </Box>
          
          {activeSessions.length === 0 ? (
            <Alert severity="info">No active sessions</Alert>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>IP Address</TableCell>
                    <TableCell>Location</TableCell>
                    <TableCell>ISP</TableCell>
                    <TableCell>Login Time</TableCell>
                    <TableCell>Duration</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {activeSessions.map((session) => (
                    <TableRow key={session.id}>
                      <TableCell>{session.ip}</TableCell>
                      <TableCell>{session.location || 'Unknown'}</TableCell>
                      <TableCell>{session.isp || 'Unknown'}</TableCell>
                      <TableCell>{new Date(session.loginTime).toLocaleString()}</TableCell>
                      <TableCell>{formatDuration(session.loginTime)}</TableCell>
                      <TableCell>
                        <IconButton
                          color="error"
                          onClick={() => setKickDialog({ open: true, session })}
                          size="small"
                        >
                          <Cancel />
                        </IconButton>
                        <IconButton
                          color="warning"
                          onClick={() => setBanDialog({ open: true, ip: session.ip })}
                          size="small"
                        >
                          <Block />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>

        {/* Activity Log Tab */}
        <TabPanel value={activeTab} index={1}>
          <Typography variant="h6" sx={{ mb: 2 }}>Recent Activity Log</Typography>
          <TableContainer component={Paper} sx={{ maxHeight: 400, overflow: 'auto' }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Timestamp</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>IP</TableCell>
                  <TableCell>Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {activityLog.slice(0, 50).map((log, index) => (
                  <TableRow key={index}>
                    <TableCell>{new Date(log.timestamp).toLocaleString()}</TableCell>
                    <TableCell>{log.action}</TableCell>
                    <TableCell>{log.ip}</TableCell>
                    <TableCell>{log.details}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Banned IPs Tab */}
        <TabPanel value={activeTab} index={2}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Banned IP Addresses</Typography>
          </Box>
          
          {bannedIPs.length === 0 ? (
            <Alert severity="info">No IP addresses are currently banned</Alert>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>IP Address</TableCell>
                    <TableCell>Banned At</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bannedIPs.map((ipData, index) => (
                    <TableRow key={index}>
                      <TableCell>{ipData.ip}</TableCell>
                      <TableCell>{new Date(ipData.bannedAt).toLocaleString()}</TableCell>
                      <TableCell>
                        <Button
                          variant="outlined"
                          color="primary"
                          size="small"
                          onClick={() => unbanIP(ipData.ip)}
                          startIcon={<CheckCircle />}
                        >
                          Unban
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>

        {/* User Management Tab */}
        <TabPanel value={activeTab} index={3}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">User Management</Typography>
            <Box>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<CloudDownload />}
                onClick={handleExportUsers}
                sx={{ mr: 1 }}
              >
                Export Users
              </Button>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<CloudUpload />}
                onClick={() => setShowImportDialog(true)}
                sx={{ mr: 1 }}
              >
                Import Users
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<PersonAdd />}
                onClick={() => setShowAddUserDialog(true)}
              >
                Add User
              </Button>
            </Box>
          </Box>



          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Username</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>{user.username}</TableCell>
                    <TableCell>
                      <Chip
                        label={user.role}
                        color={user.role === 'admin' ? 'primary' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        color="error"
                        onClick={() => {
                          setDeleteUserId(user.id);
                          setShowDeleteDialog(true);
                        }}
                        disabled={user.username === 'admin'}
                        size="small"
                      >
                        <Delete />
                      </IconButton>
                      <FormControl size="small" sx={{ minWidth: 120, ml: 1 }}>
                        <InputLabel>Role</InputLabel>
                        <Select
                          value={user.role}
                          label="Role"
                          onChange={(e) => updateUserRole(user.id, e.target.value)}
                          disabled={user.username === 'admin'}
                        >
                          <MenuItem value="user">User</MenuItem>
                          <MenuItem value="admin">Admin</MenuItem>
                        </Select>
                      </FormControl>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>
      </Card>

      {/* Add User Dialog */}
      <Dialog open={showAddUserDialog} onClose={() => setShowAddUserDialog(false)}>
        <DialogTitle>Add New User</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Username"
            type="text"
            fullWidth
            variant="outlined"
            value={newUsername}
            onChange={(e) => setNewUsername(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Password"
            type="password"
            fullWidth
            variant="outlined"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth>
            <InputLabel>Role</InputLabel>
            <Select
              value={newUserRole}
              label="Role"
              onChange={(e) => setNewUserRole(e.target.value)}
            >
              <MenuItem value="user">User</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddUserDialog(false)}>Cancel</Button>
          <Button onClick={handleAddUser} variant="contained" color="primary">
            Add User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete User Dialog */}
      <Dialog open={showDeleteDialog} onClose={() => setShowDeleteDialog(false)}>
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this user? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleDeleteUser} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import Users Dialog */}
      <Dialog open={showImportDialog} onClose={() => setShowImportDialog(false)}>
        <DialogTitle>Import Users</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Select a JSON file containing an array of user objects with username, password, and role properties.
          </DialogContentText>
          <input
            type="file"
            accept=".json"
            onChange={handleFileSelect}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowImportDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleImportUsers} 
            variant="contained" 
            color="primary"
            disabled={!importFile}
          >
            Import
          </Button>
        </DialogActions>
      </Dialog>

      {/* Kick Session Dialog */}
      <Dialog open={kickDialog.open} onClose={() => setKickDialog({ open: false, session: null })}>
        <DialogTitle>Kick User Session</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to kick the session from {kickDialog.session?.ip}?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setKickDialog({ open: false, session: null })}>Cancel</Button>
          <Button onClick={handleKickSession} color="error" variant="contained">
            Kick
          </Button>
        </DialogActions>
      </Dialog>

      {/* Ban IP Dialog */}
      <Dialog open={banDialog.open} onClose={() => setBanDialog({ open: false, ip: '' })}>
        <DialogTitle>Ban IP Address</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to ban the IP address {banDialog.ip}?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBanDialog({ open: false, ip: '' })}>Cancel</Button>
          <Button onClick={handleBanIP} color="error" variant="contained">
            Ban IP
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AdminPanel;
